import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  axesOf,
  axisOptionLabelOf,
  editionsOf,
  type FacetedSetup,
  type FlatMember,
  familyOf,
  locate,
  reconcileVariant,
  resolveId,
  searchSetups,
  triggerLabel,
} from "@/platform/catalog/setup-model";
import type { VariantTags } from "@/platform/thecore/ingest";
import thecoreMark from "@/render/assets/thecore-mark.png";
import { LearnTheCore } from "./LearnTheCore";
import { UploadButton } from "./UploadButton";

/**
 * The Setup picker: a compact trigger in the control bar that opens a faceted popover. It replaces the
 * flat Profile <select> that listed every SC2 source at once. The popover is data-driven off the
 * catalog faceting seam (editionAxes / facet): pick a family (TheCore / Blizzard / My profiles / Personal),
 * an edition, then only the axes that edition declares. It collapses to a bare menu for simple games
 * (Destiny / Deadlock) that have no variant axes. One adaptive component for all three sites the old
 * ProfilePicker filled (control bar + both Compare header cards). Draft selections update the popover's
 * resolved footer live and commit to the board on "View profile" (or on picking a flat leaf); the closed
 * trigger always shows the fully-resolved committed setup.
 */

// --- model the page feeds in ------------------------------------------------

export interface SetupPickerModel {
  kind: "faceted" | "simple";
  /** The committed selection's id (a source id for SC2, a Profile id for a simple game). */
  activeId: string;
  // faceted (SC2):
  /** TheCore + Blizzard setups, carrying variant tags - the faceting list. */
  setups: readonly FacetedSetup[];
  /** The Personal family (Edennil); empty on the Compare B-side, where pinned sources are dropped. */
  personal: readonly FlatMember[];
  /** Uploaded .SC2Hotkeys file(s) - the "My profiles" family. */
  uploads: readonly FlatMember[];
  // simple (Destiny / Deadlock):
  /** The bare-menu heading, e.g. "Destiny 2 profiles". */
  simpleLabel?: string;
  /** A simple game's Profiles (built-ins + any upload). */
  simpleProfiles?: readonly FlatMember[];
  /** The dropzone copy, e.g. "Upload your cvars.xml". */
  uploadHint?: string;
}

export interface SetupPickerProps {
  ariaLabel: string;
  model: SetupPickerModel;
  /** Live selection handler. Omit for an inert locked trigger (Compare B mirrors A). */
  onChange?: (id: string) => void;
  /** Opens the shared import pipeline. Omit to hide the upload affordance. */
  onUpload?: () => void;
  /** Client-side navigation for the TheCore "Learn" footer link. */
  onNavigate?: (to: string) => void;
  /** The guided layer: star the recommended edition + a first-run hint. Default on. */
  guided?: boolean;
}

// --- family presentation config ---------------------------------------------

interface FamilyDef {
  key: string;
  /** Variant source for a faceted family; absent for a flat family. */
  source?: string;
  name: string;
  editionLabel?: string;
  editionStyle?: "chips" | "segmented";
  recommended?: string;
  mark: ReactNode;
}

const FACETED_FAMILIES: Record<string, FamilyDef> = {
  TheCore: {
    key: "TheCore",
    source: "TheCore",
    name: "TheCore",
    editionLabel: "Version",
    editionStyle: "chips",
    recommended: "6",
    mark: (
      <img
        className="stp-fam-img"
        src={thecoreMark}
        alt=""
        aria-hidden="true"
        width={16}
        height={16}
      />
    ),
  },
  Blizzard: {
    key: "Blizzard",
    source: "Blizzard",
    name: "Blizzard",
    editionLabel: "Preset",
    editionStyle: "segmented",
    recommended: "standard",
    mark: <ShieldIcon />,
  },
};

const UPLOADS_FAMILY: FamilyDef = {
  key: "uploads",
  name: "Uploads",
  mark: <UploadIcon />,
};
const PERSONAL_FAMILY: FamilyDef = {
  key: "personal",
  name: "Community",
  mark: <PersonIcon />,
};

/** TheCore editions display ascending (1-6); Blizzard keeps its in-game order (Standard, Classic, Grid). */
function orderedEditions(fam: FamilyDef, setups: readonly FacetedSetup[]): string[] {
  const editions = editionsOf(setups);
  if (fam.key === "TheCore") return [...editions].sort((a, b) => Number(a) - Number(b));
  return editions;
}

// --- draft state ------------------------------------------------------------

interface Draft {
  familyKey: string;
  variant: VariantTags; // only meaningful for a faceted family
}

/** Seed the draft from the committed selection: locate it among the faceted setups, uploads, or Personal. */
function seedDraft(model: SetupPickerModel): Draft {
  const located = locate(model.setups, model.activeId);
  if (located) return { familyKey: located.source, variant: located.variant };
  if (model.uploads.some((u) => u.id === model.activeId)) return blankDraft(model, "uploads");
  if (model.personal.some((p) => p.id === model.activeId)) return blankDraft(model, "personal");
  // fall back to the first faceted family present
  const first = orderedFamilies(model)[0];
  return first?.source ? blankDraft(model, first.key) : blankDraft(model, "uploads");
}

/** A fresh draft for a family: for a faceted family, its recommended (or first) edition, axes at defaults. */
function blankDraft(model: SetupPickerModel, familyKey: string): Draft {
  const fam = familyDefFor(familyKey);
  if (fam?.source) {
    const setups = familyOf(model.setups, fam.source);
    const editions = orderedEditions(fam, setups);
    const edition =
      fam.recommended && editions.includes(fam.recommended) ? fam.recommended : (editions[0] ?? "");
    return { familyKey, variant: reconcileVariant(setups, edition, {}) };
  }
  return { familyKey, variant: { edition: "", hand: "" } };
}

function familyDefFor(key: string): FamilyDef | undefined {
  return (
    FACETED_FAMILIES[key] ??
    (key === "uploads" ? UPLOADS_FAMILY : key === "personal" ? PERSONAL_FAMILY : undefined)
  );
}

/** The families to show, in rail order: faceted families that have setups, then Community, then Uploads. */
function orderedFamilies(model: SetupPickerModel): FamilyDef[] {
  const out: FamilyDef[] = [];
  for (const source of ["TheCore", "Blizzard"]) {
    const def = FACETED_FAMILIES[source];
    if (def && familyOf(model.setups, source).length > 0) out.push(def);
  }
  if (model.personal.length > 0) out.push(PERSONAL_FAMILY);
  out.push(UPLOADS_FAMILY);
  return out;
}

function familyCount(model: SetupPickerModel, fam: FamilyDef): number {
  if (fam.source) return familyOf(model.setups, fam.source).length;
  if (fam.key === "uploads") return model.uploads.length;
  return model.personal.length;
}

// --- top-level component ----------------------------------------------------

export function SetupPicker({
  ariaLabel,
  model,
  onChange,
  onUpload,
  onNavigate,
  guided = true,
}: SetupPickerProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => seedDraft(model));
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();

  // reseed the draft whenever the popover opens or the committed selection changes underneath it
  const openPopover = () => {
    setDraft(seedDraft(model));
    setOpen(true);
  };

  // Esc (capture-phase, so the app's global keydown handler doesn't also read it) + pointerdown
  // click-away. Same shape as ReadoutStrip.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      e.stopImmediatePropagation();
      e.preventDefault();
    };
    const onDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("pointerdown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  const commit = (id: string | undefined) => {
    if (id && onChange) onChange(id);
    setOpen(false);
  };

  const inert = !onChange;

  return (
    <span className="stp-wrap" ref={wrapRef}>
      <SetupTrigger
        ariaLabel={ariaLabel}
        model={model}
        expanded={open}
        panelId={panelId}
        inert={inert}
        onToggle={() => (open ? setOpen(false) : openPopover())}
      />
      {/* the upload affordance stays out in the control bar (one click, always visible), beside the
          trigger - the popover's dropzone is the same path, just where uploaded files also live */}
      {onUpload ? <UploadButton title="Upload a hotkey file" onClick={onUpload} /> : null}
      {open ? (
        <div
          className="stp-pop"
          id={panelId}
          role="dialog"
          aria-label={ariaLabel}
          {...(model.kind === "simple" ? { "data-simple": "" } : {})}
        >
          <span className="stp-beak" />
          {model.kind === "simple" ? (
            <SimpleMenu model={model} onUpload={onUpload} onPick={commit} />
          ) : (
            <FacetedPanel
              model={model}
              draft={draft}
              setDraft={setDraft}
              guided={guided}
              onUpload={onUpload}
              onNavigate={onNavigate}
              onCommit={commit}
            />
          )}
        </div>
      ) : null}
    </span>
  );
}

// --- trigger ----------------------------------------------------------------

function SetupTrigger({
  ariaLabel,
  model,
  expanded,
  panelId,
  inert,
  onToggle,
}: {
  ariaLabel: string;
  model: SetupPickerModel;
  expanded: boolean;
  panelId: string;
  inert: boolean;
  onToggle: () => void;
}) {
  const face = triggerFace(model);
  return (
    <button
      type="button"
      className={`stp-trig${face.live ? " live" : ""}${face.placeholder ? " placeholder" : ""}`}
      aria-label={ariaLabel}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      aria-controls={expanded ? panelId : undefined}
      // the committed selection's id, exposed like a <select>'s value for deep-linking + tests
      data-value={model.activeId}
      disabled={inert}
      onClick={onToggle}
    >
      {face.mark ? <span className="stp-mk">{face.mark}</span> : null}
      <span className={`stp-main${face.mono ? " mono" : ""}`}>{face.main}</span>
      {face.pills.length > 0 || face.hand ? (
        <span className="stp-tags">
          {face.pills.map((p) => (
            <span className="stp-tag" key={p}>
              {p}
            </span>
          ))}
          {face.hand ? <span className="stp-tag hand">{face.hand}</span> : null}
        </span>
      ) : null}
      <CaretIcon />
    </button>
  );
}

interface TriggerFace {
  mark: ReactNode;
  main: string;
  pills: string[];
  hand?: string;
  mono?: boolean;
  live: boolean;
  placeholder?: boolean;
}

/** The closed trigger face for the committed selection: faceted (mark+main+pills+hand), upload (mono
 * filename), Personal (name), simple-game (name), or the first-run placeholder. */
function triggerFace(model: SetupPickerModel): TriggerFace {
  if (model.kind === "simple") {
    const p =
      model.simpleProfiles?.find((x) => x.id === model.activeId) ?? model.simpleProfiles?.[0];
    return { mark: null, main: p?.name ?? "Choose a profile", pills: [], live: !!p };
  }
  const located = locate(model.setups, model.activeId);
  if (located) {
    const fam = FACETED_FAMILIES[located.source];
    const label = triggerLabel(fam?.name ?? located.source, located.variant);
    return {
      mark: fam?.mark ?? null,
      main: label.main,
      pills: label.pills,
      hand: label.hand,
      live: true,
    };
  }
  const upload = model.uploads.find((u) => u.id === model.activeId);
  if (upload) return { mark: <DocIcon />, main: upload.name, pills: [], mono: true, live: true };
  const personal = model.personal.find((p) => p.id === model.activeId);
  if (personal) return { mark: <PersonIcon />, main: personal.name, pills: [], live: true };
  return { mark: null, main: "Choose a profile", pills: [], live: false, placeholder: true };
}

// --- faceted panel ----------------------------------------------------------

function FacetedPanel({
  model,
  draft,
  setDraft,
  guided,
  onUpload,
  onNavigate,
  onCommit,
}: {
  model: SetupPickerModel;
  draft: Draft;
  setDraft: (d: Draft) => void;
  guided: boolean;
  onUpload?: () => void;
  onNavigate?: (to: string) => void;
  onCommit: (id: string | undefined) => void;
}) {
  const [query, setQuery] = useState("");
  const families = orderedFamilies(model);
  const fam = familyDefFor(draft.familyKey) ?? families[0];
  const searching = query.trim().length > 0;
  const results = useMemo(
    () =>
      searching ? searchSetups(model.setups, [...model.uploads, ...model.personal], query) : [],
    [searching, query, model.setups, model.uploads, model.personal],
  );

  const pickFamily = (key: string) => {
    setQuery("");
    setDraft(seedFamily(model, key));
  };

  return (
    <>
      <div className="stp-head">
        <div className="stp-search">
          <SearchIcon />
          <input
            className="stp-search-input"
            type="text"
            value={query}
            placeholder="Search profiles"
            aria-label="Search profiles"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && results[0]) onCommit(results[0].id);
            }}
          />
          <span className="stp-esc">Esc</span>
        </div>
      </div>
      {searching ? (
        <SearchResults results={results} activeId={model.activeId} onPick={onCommit} />
      ) : (
        <div className="stp-body">
          <div className="stp-rail">
            <div className="stp-rail-h">Source</div>
            {families.map((f) => (
              <button
                type="button"
                key={f.key}
                aria-pressed={f.key === fam?.key}
                data-family={f.key}
                className={`stp-rail-item${f.key === fam?.key ? " on" : ""}`}
                onClick={() => pickFamily(f.key)}
              >
                <span className="stp-fic">{f.mark}</span>
                <span className="stp-rail-name">{f.name}</span>
                <span className="stp-rail-count">{familyCount(model, f)}</span>
              </button>
            ))}
          </div>
          <div className="stp-pane">
            {fam?.source ? (
              <FacetedPane
                model={model}
                fam={fam}
                draft={draft}
                setDraft={setDraft}
                guided={guided}
              />
            ) : fam?.key === "uploads" ? (
              <UploadsPane model={model} onUpload={onUpload} onPick={onCommit} />
            ) : (
              <FlatPane
                title="Community profiles"
                members={model.personal}
                activeId={model.activeId}
                onPick={onCommit}
              />
            )}
          </div>
        </div>
      )}
      <Footer model={model} fam={fam} draft={draft} onNavigate={onNavigate} onCommit={onCommit} />
    </>
  );
}

/** A fresh draft for a family switch (keeps the module's seed logic in one place). */
function seedFamily(model: SetupPickerModel, key: string): Draft {
  return blankDraft(model, key);
}

function FacetedPane({
  model,
  fam,
  draft,
  setDraft,
  guided,
}: {
  model: SetupPickerModel;
  fam: FamilyDef;
  draft: Draft;
  setDraft: (d: Draft) => void;
  guided: boolean;
}) {
  const setups = familyOf(model.setups, fam.source as string);
  const editions = orderedEditions(fam, setups);
  const edition = draft.variant.edition;
  const axes = axesOf(setups, edition);

  const pickEdition = (ed: string) => {
    setDraft({ familyKey: fam.key, variant: reconcileVariant(setups, ed, draft.variant) });
  };
  const pickAxis = (key: keyof VariantTags, value: string) => {
    setDraft({ familyKey: fam.key, variant: { ...draft.variant, [key]: value } });
  };

  return (
    <>
      <div className="stp-axis">
        <div className="stp-axis-lbl">
          {fam.editionLabel}
          {guided && fam.key === "TheCore" ? (
            <span className="stp-hint">Recommended version: {fam.recommended}</span>
          ) : null}
        </div>
        {fam.editionStyle === "chips" ? (
          <div className="stp-edrail">
            {editions.map((ed) => (
              <button
                type="button"
                key={ed}
                aria-pressed={ed === edition}
                aria-label={`${fam.editionLabel} ${ed}${guided && ed === fam.recommended ? " (recommended)" : ""}`}
                title={guided && ed === fam.recommended ? "Recommended" : undefined}
                data-ed={ed}
                className={`stp-ed${ed === edition ? " on" : ""}`}
                onClick={() => pickEdition(ed)}
              >
                <span className="stp-ed-n">{ed}</span>
                {guided && ed === fam.recommended ? (
                  <span className="stp-star" aria-hidden="true">
                    <StarIcon />
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        ) : (
          <Segmented
            axisLabel={fam.editionLabel as string}
            options={editions}
            value={edition}
            onPick={pickEdition}
            recommended={guided ? fam.recommended : undefined}
          />
        )}
      </div>
      {axes.map((axis) => (
        <div className="stp-axis" key={axis.key}>
          <div className="stp-axis-lbl">{AXIS_TITLES[axis.key] ?? axis.key}</div>
          <Segmented
            axisLabel={AXIS_TITLES[axis.key] ?? axis.key}
            options={axis.options}
            value={draft.variant[axis.key] ?? ""}
            onPick={(v) => pickAxis(axis.key, v)}
            axisKey={axis.key}
          />
        </div>
      ))}
    </>
  );
}

const AXIS_TITLES: Record<string, string> = {
  race: "Race",
  size: "Hand size",
  modifier: "Modifier",
  hand: "Mouse hand",
};

function Segmented({
  axisLabel,
  options,
  value,
  onPick,
  axisKey,
  recommended,
}: {
  axisLabel: string;
  options: string[];
  value: string;
  onPick: (value: string) => void;
  axisKey?: string;
  recommended?: string;
}) {
  return (
    <div className="stp-seg">
      {options.map((opt) => {
        const label = axisKey ? axisOptionLabelOf(axisKey, opt) : titleize(opt);
        return (
          <button
            type="button"
            key={opt}
            aria-pressed={opt === value}
            aria-label={`${axisLabel}: ${label}`}
            data-opt={opt}
            className={`stp-seg-btn${opt === value ? " on" : ""}`}
            onClick={() => onPick(opt)}
          >
            {label}
            {recommended === opt ? <span className="stp-seg-star" aria-hidden="true" /> : null}
          </button>
        );
      })}
    </div>
  );
}

const titleize = (v: string) => v.charAt(0).toUpperCase() + v.slice(1);

// --- flat panes (uploads / personal) + search + simple menu -----------------

function UploadsPane({
  model,
  onUpload,
  onPick,
}: {
  model: SetupPickerModel;
  onUpload?: () => void;
  onPick: (id: string) => void;
}) {
  return (
    <div className="stp-flat">
      <div className="stp-axis-lbl">Your uploaded files</div>
      <div className="stp-filelist">
        {model.uploads.map((u) => (
          <FileRow key={u.id} member={u} active={u.id === model.activeId} onPick={onPick} />
        ))}
      </div>
      {onUpload ? (
        <button type="button" className="stp-dropzone" onClick={onUpload}>
          <UploadIcon />
          Upload a .SC2Hotkeys file, or drop it anywhere
        </button>
      ) : null}
    </div>
  );
}

function FlatPane({
  title,
  members,
  activeId,
  onPick,
}: {
  title: string;
  members: readonly FlatMember[];
  activeId: string;
  onPick: (id: string) => void;
}) {
  return (
    <div className="stp-flat">
      <div className="stp-axis-lbl">{title}</div>
      <div className="stp-filelist">
        {members.map((m) => (
          <FileRow key={m.id} member={m} active={m.id === activeId} onPick={onPick} plain />
        ))}
      </div>
    </div>
  );
}

function FileRow({
  member,
  active,
  onPick,
  plain,
}: {
  member: FlatMember;
  active: boolean;
  onPick: (id: string) => void;
  plain?: boolean;
}) {
  return (
    <button
      type="button"
      className={`stp-file-row${active ? " on" : ""}`}
      aria-pressed={active}
      data-id={member.id}
      onClick={() => onPick(member.id)}
    >
      <span className="stp-fdoc">{plain ? <PersonIcon /> : <DocIcon />}</span>
      <span className={`stp-fname${plain ? "" : " mono"}`}>{member.name}</span>
    </button>
  );
}

function SearchResults({
  results,
  activeId,
  onPick,
}: {
  results: { id: string; name: string; tags: string[] }[];
  activeId: string;
  onPick: (id: string) => void;
}) {
  return (
    <div className="stp-results" role="listbox" aria-label="Search results">
      {results.length === 0 ? <div className="stp-noresults">No setups match</div> : null}
      {results.map((r) => (
        <button
          type="button"
          key={r.id}
          role="option"
          aria-selected={r.id === activeId}
          data-id={r.id}
          className={`stp-res${r.id === activeId ? " on" : ""}`}
          onClick={() => onPick(r.id)}
        >
          <span className="stp-res-name">{r.name}</span>
          {r.tags.length > 0 ? <span className="stp-res-tags">{r.tags.join(" ")}</span> : null}
        </button>
      ))}
    </div>
  );
}

function SimpleMenu({
  model,
  onUpload,
  onPick,
}: {
  model: SetupPickerModel;
  onUpload?: () => void;
  onPick: (id: string) => void;
}) {
  return (
    <div className="stp-pane simple">
      <div className="stp-axis-lbl">{model.simpleLabel ?? "Profiles"}</div>
      <div className="stp-filelist">
        {(model.simpleProfiles ?? []).map((p) => (
          <FileRow key={p.id} member={p} active={p.id === model.activeId} onPick={onPick} plain />
        ))}
      </div>
      {onUpload ? (
        <button type="button" className="stp-dropzone" onClick={onUpload}>
          <UploadIcon />
          {model.uploadHint ?? "Upload a file"}
        </button>
      ) : null}
    </div>
  );
}

// --- footer -----------------------------------------------------------------

function Footer({
  model,
  fam,
  draft,
  onNavigate,
  onCommit,
}: {
  model: SetupPickerModel;
  fam: FamilyDef | undefined;
  draft: Draft;
  onNavigate?: (to: string) => void;
  onCommit: (id: string | undefined) => void;
}) {
  const resolved = resolvedFooter(model, fam, draft);
  return (
    <div className="stp-foot">
      <div className="stp-resolved">
        <div className={`stp-r-name${resolved.mono ? " mono" : ""}`}>{resolved.name}</div>
        {resolved.sub ? <div className="stp-r-sub">{resolved.sub}</div> : null}
      </div>
      <div className="stp-foot-actions">
        {fam?.key === "TheCore" ? <LearnTheCore onNavigate={onNavigate} /> : null}
        <button
          type="button"
          className="stp-view"
          onClick={() => onCommit(resolved.id)}
          disabled={!resolved.id}
        >
          <ArrowIcon />
          View profile
        </button>
      </div>
    </div>
  );
}

interface Resolved {
  id: string | undefined;
  name: string;
  sub?: string;
  mono?: boolean;
}

function resolvedFooter(
  model: SetupPickerModel,
  fam: FamilyDef | undefined,
  draft: Draft,
): Resolved {
  if (fam?.source) {
    const setups = familyOf(model.setups, fam.source);
    const id = resolveId(setups, draft.variant);
    const label = triggerLabel(fam.name, draft.variant);
    const parts = [label.main, ...label.pills, label.hand].filter(Boolean) as string[];
    return {
      id,
      name: parts.join(", "),
      sub: fam.key === "Blizzard" ? "default hotkeys" : undefined,
    };
  }
  if (fam?.key === "uploads") {
    const active = model.uploads.find((u) => u.id === model.activeId) ?? model.uploads[0];
    return active
      ? { id: active.id, name: active.name, sub: "your file", mono: true }
      : { id: undefined, name: "No file uploaded yet" };
  }
  const active = model.personal.find((p) => p.id === model.activeId) ?? model.personal[0];
  return active ? { id: active.id, name: active.name } : { id: undefined, name: "" };
}

// --- icons (inline lucide-style, color via currentColor) --------------------

function CaretIcon() {
  return (
    <svg className="stp-car" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  );
}
function ShieldIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3l7 4v6c0 4-3 6.5-7 8-4-1.5-7-4-7-8V7z" />
    </svg>
  );
}
function UploadIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 15V4M8 8l4-4 4 4" />
      <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}
function PersonIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
    </svg>
  );
}
function DocIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </svg>
  );
}
function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l2.9 6.3 6.9.7-5.2 4.6 1.5 6.8L12 17.8 5.9 20.4l1.5-6.8L2.2 9l6.9-.7z" />
    </svg>
  );
}
function ArrowIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
